import { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import axios from 'axios';

const OrgContext = createContext(null);

export function useOrg() {
  const ctx = useContext(OrgContext);
  // Return safe defaults if context is not available
  if (!ctx) return { isOrgUser: false, orgSlug: null, orgName: null, orgRole: null, orgId: null, orgLogoUrl: null, orgBrandColor: '#7C3AED', orgLoading: false };
  return ctx;
}

/**
 * OrgContextProvider — loads the user's org membership on mount.
 * Provides orgSlug, orgName, orgRole, orgId to all children.
 * If the user is not an org member, all values are null.
 */
export function OrgContextProvider({ children }) {
  const { user } = useAuth();
  const [orgData, setOrgData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchOrgStatus();
    } else {
      setOrgData(null);
      setLoading(false);
    }
  }, [user]);

  const fetchOrgStatus = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const res = await axios.get('/api/org/onboard/status', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const memberships = res.data.memberships || [];
      if (memberships.length > 0) {
        const m = memberships[0];
        setOrgData({
          orgId: m.org_id,
          orgSlug: m.org_slug,
          orgName: m.org_name,
          orgRole: m.role,
          orgLogoUrl: m.logo_url || null,
          orgBrandColor: m.brand_color || '#7C3AED',
        });
      } else {
        setOrgData(null);
      }
    } catch (err) {
      setOrgData(null);
    } finally {
      setLoading(false);
    }
  };

  const value = orgData
    ? { ...orgData, isOrgUser: true, orgLoading: loading }
    : { orgId: null, orgSlug: null, orgName: null, orgRole: null, isOrgUser: false, orgLoading: loading };

  return (
    <OrgContext.Provider value={value}>
      {children}
    </OrgContext.Provider>
  );
}
